import { FC, useMemo } from 'react';
import { Typography } from '@mui/material';

import { PassengerBookingView } from '@mopla/data-models';
import { isSpecialSchoolTrafficItinerary } from '@mopla/utils';

import {
	BookedTicketPassengersSummary,
	IBookedTicketPassengersSummaryProps,
} from '../../../';

import { PassengerInfoView } from './ui/PassengerInfoView';

export interface PassengerInfoBlockProps {
	bookedItinerary: PassengerBookingView;
}

/**
 * School rides carry no QR code, so the booking number is appended to the
 * passenger name (BE sends "Schülerfahrt" as the name) to keep it visible.
 */
const createRenderFn = (
	bookingNumber?: string
): IBookedTicketPassengersSummaryProps['render'] => {
	return ({ mainPassengerName, attendees, luggage }) => (
		<>
			<Typography variant="body2">
				{bookingNumber
					? `${mainPassengerName}: ${bookingNumber}`
					: mainPassengerName}
			</Typography>
			{attendees && <Typography variant="body2">{attendees}</Typography>}
			{luggage && <Typography variant="body2">{luggage}</Typography>}
		</>
	);
};

export const PassengerInfoBlock: FC<PassengerInfoBlockProps> = ({
	bookedItinerary,
}) => {
	const mainPassenger = useMemo(() => {
		return bookedItinerary.passengerList?.find((el) => el.isMainPassenger);
	}, [bookedItinerary?.passengerList]);

	const attendeesCount =
		(bookedItinerary.passengerList?.length || 1) +
		(bookedItinerary.toddlerSeats || 0);

	const isSpecialSchoolTraffic = isSpecialSchoolTrafficItinerary(
		bookedItinerary.itinerary
	);

	return (
		<PassengerInfoView
			mainPassenger={mainPassenger}
			attendeesCount={attendeesCount}
			passengersSummary={
				<BookedTicketPassengersSummary
					booking={bookedItinerary}
					render={createRenderFn(
						isSpecialSchoolTraffic ? bookedItinerary.bookingNumber : undefined
					)}
				/>
			}
		/>
	);
};
